import { useState } from "react";
import { Sparkles } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";
import {
  useFormatkaManualFields,
  useFormatkaTemplates,
  useSaveFormatkaProjectFields,
} from "../../hooks";

/**
 * Formatki dla działek pakietu bez wychodzenia z wniosku: wybór szablonu i skali,
 * doprecyzowanie wartości #zmiennych# tabelki, generacja ZIP-a w tle. Ramki
 * (prostokąty arkuszy) rozmieszcza automat — po utworzeniu zestaw można dopracować
 * w kreatorze (link pokazuje strona po zakończeniu generowania).
 *
 * Pola ręczne: pokazujemy WYŁĄCZNIE zmienne, których backend nie wypełni sam
 * (data, skala, numeracja arkuszy i dane projektu liczą się automatycznie);
 * wpisane wartości można zapamiętać w bazie dla projektu.
 */
export function GenerateFormatkiModal({
  connectionId,
  communes = [],
  onClose,
  onStart,
  starting,
}: {
  connectionId: string;
  // Gminy działek pakietu (bez duplikatów) — na jednym terenie bywają DWIE różne
  // gminy, więc przed generacją musi być widać, dla jakiej gminy powstaje mapa.
  communes?: string[];
  onClose: () => void;
  // rodzic startuje mutację (ma polling i obsługę błędów) — modal oddaje parametry
  onStart: (opts: {
    template?: string; scale_denominator?: number; field_values?: Record<string, string>;
    include_orientation?: boolean;
  }) => void;
  starting: boolean;
}) {
  const { data: templates, isLoading } = useFormatkaTemplates();
  const [templateId, setTemplateId] = useState("");
  const [scale, setScale] = useState(500);
  const [includeOrientation, setIncludeOrientation] = useState(true);
  const [rememberForProject, setRememberForProject] = useState(true);
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const manualFields = useFormatkaManualFields(templateId, connectionId);
  const saveProjectFields = useSaveFormatkaProjectFields(connectionId);
  const selected = (templates ?? []).find((t) => t.id === templateId);

  // Wartości WIDOCZNE w polach (wpisane teraz LUB już zapamiętane) — pusty filtr.
  const effectiveValues = () => {
    const out: Record<string, string> = {};
    for (const f of manualFields.data?.manual ?? []) {
      const v = (fieldValues[f.name] ?? f.value ?? "").trim();
      if (v) out[f.name] = v;
    }
    return out;
  };

  // Zapis „zapamiętaj dla projektu" — czekamy na niego, żeby POST nie zginął przy
  // zamknięciu modala; best-effort (nie blokuje dalszej akcji przy błędzie).
  const persistFields = async () => {
    if (!rememberForProject) return;
    const values = effectiveValues();
    if (Object.keys(values).length) {
      try {
        await saveProjectFields.mutateAsync(values);
      } catch {
        /* zapis best-effort — generacja/zamknięcie i tak idą dalej */
      }
    }
  };

  const start = async () => {
    await persistFields();
    const clean = effectiveValues();
    onStart({
      template: templateId || undefined,
      scale_denominator: scale,
      field_values: Object.keys(clean).length ? clean : undefined,
      include_orientation: includeOrientation,
    });
  };

  // Zamknięcie zapisuje wpisane wartości (bez konieczności generowania ZIP-a).
  const closeWithPersist = async () => {
    await persistFields();
    onClose();
  };

  return (
    <Modal open onClose={closeWithPersist} title="Generuj formatki dla działek pakietu" size="lg">
      <div className="space-y-3 text-xs">
        <p className="text-[var(--color-text-muted)]">
          Prostokąty arkuszy rozmieszcza automat na działkach pakietu (wspólny zestaw
          urzędowy). Wynik to ZIP z DXF do splotowania — po wygenerowaniu pobierzesz go
          w „Znalezionych planach”, a wgrane PDF-y arkuszy scalą się i dołączą same.
        </p>

        {/* Dla jakiej gminy powstaje mapa — na jednym terenie bywają dwie różne gminy,
            więc bez tej etykiety łatwo splotować mapę pod niewłaściwy urząd. */}
        {communes.length > 0 && (
          <p
            className={
              communes.length > 1
                ? "rounded border border-amber-300 bg-amber-50 px-2 py-1 text-amber-700"
                : "rounded border border-[var(--color-border-muted)] bg-[var(--color-bg-elevated)] px-2 py-1 text-[var(--color-text-secondary)]"
            }
          >
            {communes.length > 1 ? (
              <>
                ⚠ Działki pakietu leżą w <b>{communes.length} gminach</b>:{" "}
                <b>{communes.join(", ")}</b> — sprawdź, czy zakres wniosku jest właściwy.
              </>
            ) : (
              <>
                Mapa generowana dla gminy: <b>{communes[0]}</b>
              </>
            )}
          </p>
        )}

        <label className="block">
          <span className="section-label">Szablon formatki</span>
          {isLoading ? (
            <div className="py-2"><Spinner className="h-4 w-4" /></div>
          ) : (
            <select
              className="input-base mt-1"
              value={templateId}
              onChange={(e) => { setTemplateId(e.target.value); setFieldValues({}); }}
            >
              <option value="">— auto (wg typu procedury / firmowy) —</option>
              {(templates ?? []).map((t) => (
                <option key={t.id} value={t.id}>{t.name}{t.is_company ? " (firmowy)" : ""}</option>
              ))}
            </select>
          )}
        </label>

        <label className="block">
          <span className="section-label">Skala 1:</span>
          <input
            type="number" min={10} max={100000}
            className="input-base mt-1 !w-28"
            value={scale}
            onChange={(e) => setScale(Number(e.target.value) || 500)}
          />
        </label>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={includeOrientation}
            onChange={(e) => setIncludeOrientation(e.target.checked)}
          />
          Arkusz orientacyjny — pierwszy arkusz obejmuje cały zestaw i pokazuje
          rozkład (obrysy + numery) pozostałych arkuszy
        </label>

        {selected && manualFields.isLoading && (
          <div className="py-1"><Spinner className="h-4 w-4" /></div>
        )}
        {selected && manualFields.data && (
          <div className="space-y-1.5">
            {manualFields.data.manual.length > 0 ? (
              <>
                <span className="section-label">
                  Pola do uzupełnienia ręcznie (reszta wypełnia się automatycznie)
                </span>
                <div className="max-h-56 overflow-y-auto space-y-1.5">
                  {manualFields.data.manual.map((f) => (
                    <div key={f.name} className="flex items-center gap-2">
                      <span className="font-mono text-[11px] w-56 shrink-0 truncate" title={f.name}>
                        #{f.name}#
                      </span>
                      <input
                        className="input-base flex-1 text-[11px] !py-0.5"
                        placeholder={f.value ? `zapamiętane: ${f.value}` : "wartość ręczna"}
                        value={fieldValues[f.name] ?? f.value ?? ""}
                        onChange={(e) => setFieldValues((s) => ({ ...s, [f.name]: e.target.value }))}
                      />
                    </div>
                  ))}
                </div>
                <label className="flex items-center gap-2 cursor-pointer text-[11px] text-[var(--color-text-secondary)]">
                  <input
                    type="checkbox"
                    checked={rememberForProject}
                    onChange={(e) => setRememberForProject(e.target.checked)}
                  />
                  Zapamiętaj wpisane wartości dla tego projektu (kolejne generacje
                  wypełnią się same)
                </label>
              </>
            ) : (
              <p className="text-[var(--color-text-muted)]">
                Wszystkie #zmienne# szablonu wypełnią się automatycznie (data, skala
                i numeracja liczą się per arkusz; reszta z danych projektu).
              </p>
            )}
          </div>
        )}
        {templateId === "" && (
          <p className="text-[var(--color-text-muted)]">
            Przy szablonie „auto” wartości #zmiennych# wypełnią się danymi projektu —
            wybierz konkretny szablon, żeby je doprecyzować ręcznie.
          </p>
        )}

        <div className="flex items-center gap-3 pt-1">
          <Button onClick={start} loading={starting}>
            <Sparkles size={13} className="mr-1" /> Generuj ZIP do plotowania
          </Button>
          <Button variant="secondary" onClick={closeWithPersist}>Zamknij</Button>
        </div>
      </div>
    </Modal>
  );
}
